import { useState, useEffect, useCallback } from 'react';
import Card from '../../components/ui/Card';
import Button from '../../components/ui/Button';
import { callFn, inputStyle } from '../contracts/contractsApi';

const STATE_NAMES = {
  overrun: 'Base overrun', underAttack: 'Under attack', holding: 'Holding the line', defended: 'Defended', perfect: 'Perfect wave',
  comeback: 'Comeback', mostImproved: 'Most improved', climbing: 'Climbing', perfectStreak: 'Perfect streak',
};
const EMAIL_STATUS = {
  pending: 'Pending', sending: 'Sending', sent: 'Sent', failed: 'Failed', manual: 'Test run, not emailed', no_recipients: 'No recipients (opted out or no admin)',
};

// Hold the Yard admin tools for one Ops organisation.
export default function YardPanel({ orgId }) {
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(null);
  const [msg, setMsg] = useState(null);
  const [state, setState] = useState('overrun');

  const load = useCallback(() => {
    callFn('listYardWeeks', { orgId }).then(setData).catch(e => setMsg({ tone: 'danger', text: e.message }));
  }, [orgId]);
  useEffect(load, [load]);

  const run = async (key, fn, args, ok) => {
    setBusy(key); setMsg(null);
    try {
      const r = await callFn(fn, { orgId, ...args });
      setMsg({ tone: 'success', text: ok(r) });
      load();
    } catch (e) {
      setMsg({ tone: 'danger', text: e.message });
    }
    setBusy(null);
  };

  const allStates = async () => {
    setBusy('all'); setMsg(null);
    const failed = [];
    for (const s of data.states) {
      try { await callFn('forceYardState', { orgId, state: s }); } catch (e) { failed.push(`${s}: ${e.message}`); }
    }
    setMsg(failed.length ? { tone: 'danger', text: `Some failed. ${failed.join(' ')}` } : { tone: 'success', text: `All ${data.states.length} test emails sent to dan.derry@agriware.uk.` });
    setBusy(null);
  };

  return (
    <Card>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 12, flexWrap: 'wrap' }}>
        <div>
          <div style={{ fontSize: 14 }}>Hold the Yard</div>
          <div style={{ fontSize: 11, color: 'var(--color-text-tertiary)', marginTop: 2 }}>
            Weekly check-score email to this organisation's admins, sent Mondays at 10:00. Test runs and previews go only to dan.derry@agriware.uk.
          </div>
        </div>
        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', alignItems: 'center' }}>
          <Button size="sm" loading={busy === 'export'} disabled={!!busy}
            onClick={() => run('export', 'requestYardExport', {}, r => r.outcome === 'delivered' || r.delivered ? `Exported ${r.isoWeek}. Use Preview to email it to yourself.` : `Ops did not export ${r.isoWeek || 'the week'}: ${r.outcome || r.reason || 'skipped'}.`)}>
            Run export for last week
          </Button>
          <select value={state} onChange={e => setState(e.target.value)} style={{ ...inputStyle, width: 150, height: 28, fontSize: 12 }}>
            {Object.entries(STATE_NAMES).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
          </select>
          <Button size="sm" variant="secondary" loading={busy === 'force'} disabled={!!busy}
            onClick={() => run('force', 'forceYardState', { state }, r => `Sent "${r.subject}" to ${r.to}.`)}>Send test</Button>
          <Button size="sm" variant="secondary" loading={busy === 'all'} disabled={!!busy || !data}
            onClick={() => window.confirm('Send all nine test designs to dan.derry@agriware.uk?') && allStates()}>All 9</Button>
        </div>
      </div>

      {msg && <div style={{ marginTop: 12, padding: '8px 12px', background: `var(--color-${msg.tone}-light)`, color: `var(--color-${msg.tone})`, borderRadius: 'var(--radius-md)', fontSize: 12 }}>{msg.text}</div>}

      <div style={{ marginTop: 14 }}>
        {!data ? <div style={{ fontSize: 12, color: 'var(--color-text-tertiary)' }}>Loading...</div>
          : data.weeks.length === 0 ? <div style={{ fontSize: 12, color: 'var(--color-text-tertiary)' }}>No weeks scored yet.</div>
          : data.weeks.map(w => (
            <div key={w.isoWeek} style={{ display: 'grid', gridTemplateColumns: '80px 1.3fr 70px 70px 1.6fr 90px', gap: 10, alignItems: 'center', fontSize: 12, padding: '7px 0', borderTop: '0.5px solid var(--color-border-light)' }}>
              <div style={{ fontFamily: 'var(--font-mono)' }}>{w.isoWeek}</div>
              <div>{STATE_NAMES[w.state] || w.state}{w.state !== w.baseState && <span style={{ color: 'var(--color-text-tertiary)' }}> ({STATE_NAMES[w.baseState]})</span>}</div>
              <div>{String(w.score).padStart(4, '0')}</div>
              <div>{Math.round(w.checkPct)}%</div>
              <div style={{ color: w.emailStatus === 'failed' ? 'var(--color-danger)' : 'var(--color-text-secondary)' }} title={w.emailError || ''}>
                {EMAIL_STATUS[w.emailStatus] || w.emailStatus}{w.emailStatus === 'sent' ? ` to ${w.recipients}` : ''}
              </div>
              <div style={{ textAlign: 'right' }}>
                <Button size="sm" variant="ghost" loading={busy === `p${w.isoWeek}`} disabled={!!busy}
                  onClick={() => run(`p${w.isoWeek}`, 'previewYardEmail', { isoWeek: w.isoWeek }, r => `Preview sent to ${r.to}.`)}>Preview</Button>
              </div>
            </div>
          ))}
      </div>
    </Card>
  );
}
